import { type Query, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { collectionQueryPredicate } from '../../utils/collection-query-predicate';
import { COLLECTION_SCORING_WINDOW_MS, pollWhilePending } from '../../utils/poll-while-pending';
import { type GetCollectionQuery, useGetCollectionQuery } from '../get-collection';
import { isUploadedAfterPairRequest } from './get-collection-image-pair-score';

type CollectionQuery = Query<GetCollectionQuery, unknown, GetCollectionQuery>;

/** The reads that carry a collection's pair scores, and the scorings list that names the pair's request time. */
const PAIR_SCORE_QUERIES = [
  'GetCollectionImagePairScore',
  'SearchCollectionImages',
  'SearchCollectionImagesInfinite',
  'ListCollectionScorings',
];

interface UseCollectionPairRequestWatchProps {
  workspaceId: string;
  collectionId: string;
  /**
   * When the newest upload still waiting for the pair to be requested again for it was created (API clock); `null`
   * while nothing waits, which reads nothing
   */
  awaitingSince: string | null;
}

/**
 * Waits for the API to request a collection's selected pair again for an upload, and then re-reads every pair score
 * of the collection. The API does that from the upload's landing, seconds after it, with a new request time on the
 * same scoring; until then the pair's status is not about the new image. The collection is read until its selected
 * scoring shows a request after the upload, within the scoring window from it, and the caller renders again when that
 * window ends. Selecting another scoring (a category or benchmark switch) is not a new request and re-reads nothing.
 */
function useCollectionPairRequestWatch({
  workspaceId,
  collectionId,
  awaitingSince,
}: UseCollectionPairRequestWatchProps) {
  const queryClient = useQueryClient();
  const { data } = useGetCollectionQuery(
    { workspace_id: workspaceId, collection_id: collectionId },
    {
      enabled: !!workspaceId && !!collectionId && !!awaitingSince,
      // The page has just read it; the watch only reads it again on its poll, not when it starts waiting.
      staleTime: Number.POSITIVE_INFINITY,
      refetchInterval: pollWhilePending<CollectionQuery>(
        (query) =>
          awaitingSince && isUploadedAfterPairRequest(awaitingSince, query.state.data?.last_scoring?.requested_at)
            ? [awaitingSince]
            : [],
        { staleTimeoutMs: COLLECTION_SCORING_WINDOW_MS },
      ),
    },
  );

  // The wait ends with the window though no read changes anything, so the caller renders again then and reads the
  // upload as settled.
  const [, setWindowEnded] = React.useState(0);
  React.useEffect(() => {
    if (!awaitingSince) {
      return;
    }
    const windowEndsInMs = Date.parse(awaitingSince) + COLLECTION_SCORING_WINDOW_MS - Date.now() + 1;
    const timer = setTimeout(() => setWindowEnded((count) => count + 1), Math.max(windowEndsInMs, 0));
    return () => clearTimeout(timer);
  }, [awaitingSince]);

  const scoring = data?.last_scoring ?? null;
  const seen = React.useRef({ scoring, isAwaiting: !!awaitingSince });
  React.useEffect(() => {
    const previous = seen.current;
    seen.current = { scoring, isAwaiting: !!awaitingSince };
    const isRequestedAgain =
      !!previous.scoring &&
      !!scoring &&
      previous.scoring.id === scoring.id &&
      previous.scoring.requested_at !== scoring.requested_at;
    if (previous.isAwaiting && isRequestedAgain) {
      void queryClient.invalidateQueries({ predicate: collectionQueryPredicate(PAIR_SCORE_QUERIES, collectionId) });
    }
  }, [scoring, awaitingSince, queryClient, collectionId]);
}

export { useCollectionPairRequestWatch };
export type { UseCollectionPairRequestWatchProps };
